import { useCallback, useEffect, useState } from 'react';
import { connect } from 'react-redux';

import { IKnmFormRef } from 'KnmPackage/MainModule/interfaces/IKnmFormRef';
import { getKnmFormRef } from 'KnmPackage/MainModule/selectors/knmSelectors';
import { IKnmForm } from 'KnmPackage/MainModule/interfaces/IKnmForm';
import { FieldBlock } from 'UIPackage/FormModule/components/FieldBlock';
import { IRootState } from 'CorePackage/MainModule/interfaces/IRootState';
import { Row } from 'UIPackage/FormModule/components/Row';
import { fetchNKOForeignAgentByOgrn } from 'APIPackage/CatalogsModule/services/nkoForeignAgentApi';

import { INKOForeignAgent } from '../../../APIPackage/CatalogsModule/interfaces/INKOForeignAgent';
import { TextInputField } from '../../../UIPackage/MainModule/components/TextInputField';
import { formatDateLocale } from '../../../CorePackage/MainModule/common/formatDate';
import { IInspectionResult } from '../../../APIPackage/CatalogsModule/interfaces/IInspectionResult';
import {
  DATE_FORMAT_LONG_FNS,
  SYSTEM_DATE_FORMAT_SHORT_FNS,
} from '../../../CorePackage/MainModule/constants/dateFormats';
import { KnmNkoField } from '../../../KnmPackage/MainModule/components/KnmNkoField';

interface IKnmResultItemNkoInfoProps {
  checkByInnMessage: string | undefined;
  inspectionResult?: IInspectionResult;
  name: string | undefined;
  ogrn: string | undefined;
}

interface IKnmResultItemNkoInfoStoreProps {
  formRef: IKnmFormRef | undefined;
}

const KnmResultItemNkoInfoWithoutStore = ({
  checkByInnMessage,
  inspectionResult,
  name,
  ogrn,
  formRef,
}: IKnmResultItemNkoInfoProps & IKnmResultItemNkoInfoStoreProps) => {
  const [nkoData, setNkoData] = useState<INKOForeignAgent | undefined>(undefined);
  const [nkoContentLabel, setNkoContentLabel] = useState('');

  const sendNkoRequest = useCallback(async () => {
    if (ogrn) {
      if (ogrn[0] !== '0' && (/^[\d+]{13}$/.test(ogrn) || /^[\d+]{15}$/.test(ogrn))) {
        const result = await fetchNKOForeignAgentByOgrn(ogrn);

        setNkoData(result);
        setNkoContentLabel('Нет');
        formRef?.current.change(`${name}.isNko` as keyof IKnmForm, false);
      } else {
        setNkoContentLabel('');
        formRef?.current.change(`${name}.isNko` as keyof IKnmForm);
        setNkoData(undefined);
      }
    } else {
      setNkoContentLabel('');
      formRef?.current.change(`${name}.isNko` as keyof IKnmForm);
      setNkoData(undefined);
    }
  }, [formRef, ogrn, name]);

  useEffect(() => {
    sendNkoRequest();
  }, [sendNkoRequest]);

  useEffect(() => {
    if (nkoData && ogrn) {
      setNkoContentLabel('Да');
      formRef?.current.change(`${name}.nkoForeignAgentsRegistryNumber` as keyof IKnmForm, nkoData.registryNumber);
      formRef?.current.change(`${name}.isNko` as keyof IKnmForm, true);
      formRef?.current.change(
        `${name}.nkoForeignAgentsRegistryInclusionDate` as keyof IKnmForm,
        formatDateLocale({
          date: nkoData.inclusionDate,
          dateFormat: DATE_FORMAT_LONG_FNS,
          parseFormat: SYSTEM_DATE_FORMAT_SHORT_FNS,
        }),
      );
      if (nkoData.exclusionDate) {
        formRef?.current.change(
          `${name}.nkoForeignAgentsRegistryExclusionDate` as keyof IKnmForm,
          formatDateLocale({
            date: nkoData.exclusionDate,
            dateFormat: DATE_FORMAT_LONG_FNS,
            parseFormat: SYSTEM_DATE_FORMAT_SHORT_FNS,
          }),
        );
      } else {
        formRef?.current.change(`${name}.nkoForeignAgentsRegistryExclusionDate` as keyof IKnmForm, undefined);
      }
    } else if (ogrn && (ogrn[0] === '0' || !(/^[\d+]{13}$/.test(ogrn) || /^[\d+]{15}$/.test(ogrn)))) {
      setNkoContentLabel('');
      formRef?.current.change(`${name}.isNko` as keyof IKnmForm);
    }
  }, [nkoData, formRef, name, ogrn]);

  return (
    <>
      {/* Блок информации о наличии контролируемого лица в реестре НКО-иноагентов */}
      <Row>
        <KnmNkoField
          value={nkoContentLabel}
          inspectionResult={inspectionResult}
          id={`${name}.isNko`}
          name={`${name}.isNko`}
          warningMessage={checkByInnMessage}
          formRef={formRef}
        />
      </Row>

      {!!ogrn && !!nkoData && (
        <Row>
          <FieldBlock label="Регистрационный номер" placementLabel="left" size="half" labelSize="col-3">
            <TextInputField className="shared-row-field" name={`${name}.nkoForeignAgentsRegistryNumber`} disabled />
          </FieldBlock>
          <FieldBlock size="half">
            <Row>
              <FieldBlock label="Дата включения" placementLabel="left" size="half">
                <TextInputField
                  className="shared-row-field"
                  name={`${name}.nkoForeignAgentsRegistryInclusionDate`}
                  disabled
                />
              </FieldBlock>

              {nkoData?.exclusionDate && (
                <FieldBlock label="Дата исключения" placementLabel="left" size="half">
                  <TextInputField
                    className="shared-row-field"
                    name={`${name}.nkoForeignAgentsRegistryExclusionDate`}
                    disabled
                  />
                </FieldBlock>
              )}
            </Row>
          </FieldBlock>
        </Row>
      )}
    </>
  );
};

const mapStateToProps = (state: IRootState): IKnmResultItemNkoInfoStoreProps => ({
  formRef: getKnmFormRef(state),
});

export const KnmResultItemNkoInfo = connect(mapStateToProps)(KnmResultItemNkoInfoWithoutStore);
